import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { formatOrderDate, formatOrderTime } from '../utils/orderTime';
import './WeeksOrders.css';

const AllOrders = () => {
  const [allOrders, setAllOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewLabel, setViewLabel] = useState('All Orders');
  const { token } = useAuth();

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const fetchOrders = async (url, label) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, { headers: authHeaders });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setAllOrders(data);
      setViewLabel(label);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders('https://api.reshipesbakery.com/reshipes/getAllData', 'All Orders');
  }, [token]);

  const handleSearch = e => {
    e.preventDefault();
    fetchOrders(
      `https://api.reshipesbakery.com/reshipes/getOrdersByDateRange?startDate=${startDate}&endDate=${endDate}`,
      `${startDate} → ${endDate}`
    );
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSearch('');
    fetchOrders('https://api.reshipesbakery.com/reshipes/getAllData', 'All Orders');
  };

  const filteredOrders = allOrders
    ? search.trim()
      ? allOrders.orders.filter(o =>
          o.customerName.toLowerCase().includes(search.trim().toLowerCase())
        )
      : allOrders.orders
    : [];

  const filteredTotal = filteredOrders.reduce((sum, o) => sum + o.orderTotal, 0);

  return (
    <div className="container mt-5">
      <h1>Customer Orders — {viewLabel}</h1>

      <form onSubmit={handleSearch} className="mt-3 mb-2 d-flex align-items-end gap-3 flex-wrap">
        <div>
          <label className="form-label">From</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="form-label">To</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          Search
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
          Show All
        </button>
      </form>

      <div className="mt-3 mb-4" style={{ maxWidth: 360 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Filter by customer name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading && <div>Loading...</div>}
      {error && <div>Error: {error.message}</div>}

      {!loading && allOrders && (
        <>
          <div className="mb-4">
            <h4>
              Total: ${filteredTotal.toFixed(2)}
              {search.trim() && (
                <span className="text-muted fs-6 ms-2">
                  ({filteredOrders.length} of {allOrders.orders.length} orders)
                </span>
              )}
            </h4>
          </div>

          {filteredOrders.length === 0 ? (
            <p>No orders found.</p>
          ) : (
            filteredOrders.map(order => (
              <div key={order.orderId} className="mt-4">
                <h3>Customer Name: {order.customerName}</h3>
                <p>Order Date: {formatOrderDate(order.orderDate, order.orderTime)}</p>
                <p>Order Time: {formatOrderTime(order.orderDate, order.orderTime)}</p>
                <table className="table table-striped order-items-table">
                  <thead>
                    <tr>
                      <th>Food Name</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.size} {item.foodName}</td>
                        <td>{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p>Comments: {order.comments}</p>
                <h4>Total Amount: ${order.orderTotal.toFixed(2)}</h4>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default AllOrders;
